import { NavLink, Tooltip } from '@mantine/core';
import { CanAccess, ITreeMenu } from '@refinedev/core';
import { useThemedLayoutContext } from '@refinedev/mantine';
import { IconList } from '@tabler/icons-react';
import { useIsProvider } from 'hooks';
import { useGetUserCognitoGroup } from 'hooks/useGetUserCognitoGroup';
import { useIsAdmin } from 'hooks/useIsAdmin';
import { CSSProperties, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { useClaimTabFilterStore } from 'store/ClaimTabFilterStore';

interface SiderMenuItemsProps {
  menuItems: ITreeMenu[];
  selectedKey?: string;
  activeItemDisabled?: boolean;
  isCollapsed: boolean;
  mobileSiderOpen: boolean;
  commonNavLinkStyles: any;
  commonToolTipProps: any;
  defaultOpenKeys: string[];
}

export const SiderMenuItems: React.FC<SiderMenuItemsProps> = ({
  menuItems,
  selectedKey,
  activeItemDisabled,
  isCollapsed,
  mobileSiderOpen,
  commonNavLinkStyles,
  commonToolTipProps,
  defaultOpenKeys,
}) => {
  const cognitoGroup = useGetUserCognitoGroup();
  const { setMobileSiderOpen } = useThemedLayoutContext();
  const isAdmin = useIsAdmin();
  const isProvider = useIsProvider();
  const resetClaimTabFilters = useClaimTabFilterStore(
    (state) => state.resetClaimTabFilters
  );

  const renderMenuItems = (menu: ITreeMenu[]) => {
    const items = menu?.map((item) => {
      if (!isAdmin && item.name === 'organizations') return null;
      if (item.name === 'dashboard') return null;
      const { label, name, children } = item as any;
      const isSelected = item.key === selectedKey;
      const isParent = children.length > 0;
      const disablePointerStyle: CSSProperties =
        activeItemDisabled && isSelected ? { pointerEvents: 'none' } : {};
      return (
        <CanAccess
          key={item.key}
          resource={name.toLowerCase()}
          action="list"
          params={{
            resource: item,
          }}
        >
          <Tooltip label={label} {...commonToolTipProps}>
            <NavLink
              key={item.key}
              label={isCollapsed && !mobileSiderOpen ? null : label}
              leftSection={item.meta?.icon || <IconList size={20} />}
              active={isSelected}
              childrenOffset={isCollapsed && !mobileSiderOpen ? 0 : 12}
              defaultOpened={defaultOpenKeys.includes(item.key || '')}
              pl={isCollapsed || mobileSiderOpen ? '0.75rem' : '1.125rem'}
              data-testid={`${name}-nav-link`}
              styles={commonNavLinkStyles}
              style={disablePointerStyle}
              to={item.key}
              component={Link}
              onClick={() => {
                if (isProvider && item.name === 'claims') {
                  resetClaimTabFilters(true);
                }
                if (mobileSiderOpen) {
                  setMobileSiderOpen(false);
                }
              }}
            >
              {isParent && renderMenuItems(children)}
            </NavLink>
          </Tooltip>
        </CanAccess>
      );
    });

    return items;
  };

  const items =
    cognitoGroup !== 'PROVIDER'
      ? renderMenuItems(menuItems)
      : renderMenuItems(
          menuItems.filter(
            (item) =>
              item.name === 'statements' ||
              item.name === 'transactions' ||
              item.name === 'uploads' ||
              item.name === 'claims' ||
              item.name === 'queries' ||
              item.name === 'reports'
          )
        );

  return <Fragment>{items}</Fragment>;
};
